import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import { createJobRetryNode } from "~/components/build/Show/routes/list/-components/TreeNode/JobRetryItem";
import { CommandJob, RetryableJob } from "~/components/build/Show/lib/types";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { retryAttemptsForJob } from "~/components/build/Show/lib/jobRetryHistory";
import { CommandStep } from "~/lib/pipeline";

/**
 * Child nodes for a job/command-step row's retry history: one row per attempt,
 * oldest first, with the current attempt last. Read lazily from the store at
 * flatten time so a fetch-on-expand (or a retry-driven invalidation) refreshes
 * the list.
 *
 * `referenceJob` is the latest attempt the history hangs off. For command
 * steps the row has no job object to hand, so it's derived from the most
 * recent job in the store (command steps are single-lane, so this is exact).
 */
export function retryHistoryChildNodes(
  step: CommandStep,
  referenceJob: RetryableJob | undefined,
  collapsibleState: CollapsibleState,
): TreeNode[] {
  if (collapsibleState === CollapsibleState.Collapsed) {
    return [];
  }

  const stepJobs = (useBuildPageStore.getState().stepJobs.get(step.uuid) || []) as RetryableJob[];
  if (stepJobs.length === 0) {
    return [];
  }

  const reference = referenceJob ?? stepJobs[stepJobs.length - 1];
  const attempts = retryAttemptsForJob(stepJobs, reference);

  return attempts.map((job, index) => {
    const isLatest = index === attempts.length - 1;
    return createJobRetryNode(
      step,
      job as CommandJob,
      isLatest ? "latest" : index + 1,
      // Parallel/matrix retry histories hang off a latest-job parent with the
      // same job id. Keep the child row key unique while preserving job links
      // and active state inside the rendered row.
      isLatest && referenceJob ? `${job.id}-latest-attempt` : job.id,
    );
  });
}
